// Module 07's post-login `?param` callbacks (contract §4, ledger §A6, ORP-20221) — the v3
// `beforeEach` branches at `router/index.js:145-166` and `:210-225`, moved into their owning
// feature. The shell owns registration and the URL; this module owns only the switch.
//
// v3 fidelity, branch by branch:
//   • Neither branch redirected and neither toasted. They mutated the selection and fell through to
//     the requested route, so no handler here returns a redirect. (`?success`, `?stripe_session`
//     and friends DO redirect; they are other features' callbacks and already registered.)
//   • Neither branch removed its param from the URL either — see the strip note on
//     `registerAccountQueryCallbacks` for why that fidelity is load-bearing rather than incidental.
//   • Both guarded on the list being loaded (`accountStore.accounts && …length > 0`,
//     `workspaceStore.workspaces && …length > 0`) and on the target being IN it. An id the caller
//     cannot reach is a no-op, not an error — reproduced exactly.
//   • Both guarded on the id differing from the current one. That guard now lives inside
//     `SelectAccountFlow` / `SelectWorkspaceFlow`, which is why it is not repeated here.
//
// TWO deliberate deviations, both recorded on ORP-20221:
//   • v3's `?account` called the flow with a SYNTHETIC account, `{ id, name: '', user_id: null }`
//     (`:220-224`). `SelectAccountFlow.execute` commits its argument to the store before booting
//     (`setAccount(account)`), so the synthetic object put an account with an empty name into the
//     topbar — permanently if the boot then failed. `?account` resolves through the same membership
//     lookup as `?account_id` instead: same destination, without v3's defect.
//   • v3's `?token` (`:210-217`) is NOT ported and must not be. A bearer on a query string is what
//     `scripts/check-auth-contract.mjs:113` rejects as `[token-query-handoff]`, and v4 shipped the
//     replacement in module 06 — the one-time `?handoff` exchange registered by
//     `registerAuthQueryCallback`. Its account-delegation half is `?account`, below, which runs
//     after the exchange because handlers fire in registration order.
import { useAccount } from '../composables/useAccount'
import { useWorkspace } from '../composables/useWorkspace'

/** The subset of the shell's registrar this feature needs; `app/` supplies the real one. */
export type AccountQueryRegister = (
  param: string,
  handler: (context: { value: string }) => Promise<{ preserve: true }>,
) => void

export interface AccountQueryRuntime {
  selectAccountById: (id: number) => Promise<void>
  selectWorkspaceById: (id: number) => Promise<void>
}

/** v3 compared with `Number(...)`, so a non-numeric value matched nothing. `''` included. */
function parseId(value: string): number | null {
  const id = Number(value)
  return value.trim() !== '' && Number.isFinite(id) ? id : null
}

export function accountQueryRuntime(): AccountQueryRuntime {
  return {
    selectAccountById: async (id) => {
      const { accounts, selectAccount } = useAccount()
      const target = accounts.value.find((item) => Number(item.id) === id)
      if (target) await selectAccount(target)
    },
    selectWorkspaceById: async (id) => {
      const { workspaces, selectWorkspace } = useWorkspace()
      const target = workspaces.value.find((item) => Number(item.id) === id)
      if (target) await selectWorkspace(target)
    },
  }
}

/**
 * Every handler switches and then DECLINES the strip (`{ preserve: true }`), which is v3: these
 * params stayed in the URL and the branch simply stopped matching once the selection had changed.
 *
 * Declining is not cosmetic here — these three are the overloaded params of the app. They are a
 * switch instruction on an ordinary route, but ROUTE PAYLOAD elsewhere, owned by a surface that
 * reads them after this handler has run:
 *   • the canonical Creative Analysis ingress rewrites `/accounts/1/workspaces/7/…` into
 *     `/creative-ai?account_id=1&workspace_id=7`, i.e. it MINTS them as preserved deep-link state
 *     (`e2e/legacyIngress.spec.ts:87`);
 *   • `/callback/shopify?…&workspace_id=7` carries one as its own opaque payload, and its retry is
 *     idempotent on it (`e2e/integrationCallbacks.spec.ts:20`).
 * Consuming them stole both. The seam re-fires a preserved param on the next value or path change,
 * and the switch flows no-op when the selection already matches, so nothing runs twice.
 *
 * `?account` is registered last so auth's `?handoff` exchange — registered earlier, awaited by the
 * seam — has already swapped the session before the account it names is selected.
 */
export function registerAccountQueryCallbacks(
  register: AccountQueryRegister,
  runtime: AccountQueryRuntime = accountQueryRuntime(),
): void {
  const switchTo =
    (select: (id: number) => Promise<void>) =>
    async ({ value }: { value: string }) => {
      const id = parseId(value)
      if (id !== null) await select(id)
      return { preserve: true } as const
    }

  register('account_id', switchTo(runtime.selectAccountById))
  register('workspace_id', switchTo(runtime.selectWorkspaceById))
  register('account', switchTo(runtime.selectAccountById))
}
